import { useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  DEFAULT_LOCALE,
  getLocaleFromPathname,
  getPathWithoutLocalePrefix,
  getStoredLocale,
  setStoredLocale,
  toLocalizedPath,
  type AppLocale,
} from '@/i18n/locale';

export const useAppLocale = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const locale = getLocaleFromPathname(location.pathname) ?? getStoredLocale() ?? DEFAULT_LOCALE;
  const unprefixedPathname = getPathWithoutLocalePrefix(location.pathname);

  useEffect(() => {
    setStoredLocale(locale);
    document.documentElement.lang = locale;

    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale);
    }
  }, [i18n, locale]);

  return useMemo(
    () => ({
      locale,
      localizePath: (path: string, nextLocale: AppLocale = locale) =>
        toLocalizedPath(path, nextLocale),
      unprefixedPathname,
    }),
    [locale, unprefixedPathname],
  );
};
